// src/components/RootingInterests.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RootingInterests as RootingInterestsType, LoadingState } from '../types/types';
import { getPlayerRootingInterests } from '../api/api';
import { normalizeTeamNameForDisplay, normalizeTeamNameForComparison } from '../utils/teamUtils';
import teamsData from '../data/teams.json';
import { useConfig } from '../context/ConfigContext';
import { useRootingCache, buildRootingCacheKey } from '../hooks/useRootingCache';
import InfoTooltip from './InfoTooltip';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

interface RootingInterestsProps {
  playerName: string | null;
  playerPicks: string;
  knownWinners: string[];
  onClose: () => void;
  activePoolName?: string | null;
}

// Helper function to clean team names by removing asterisks
const cleanTeamName = (teamName: string): string => {
  if (!teamName) return '';
  // Remove asterisks from both sides of the team name
  return teamName.replace(/^\*+|\*+$/g, '').trim();
};

// Build a seed lookup: normalized team name → seed number
const seedByTeam: Record<string, number> = {};
for (const team of teamsData as { name: string; seed: number }[]) {
  seedByTeam[normalizeTeamNameForComparison(team.name)] = team.seed;
}

const getTeamSeedNumber = (teamName: string): number | null => {
  const normalized = normalizeTeamNameForComparison(teamName);
  return seedByTeam[normalized] ?? null;
};

// Format a team name with its seed prefix, e.g. "(1) Auburn"
const formatTeamWithSeed = (teamName: string): string => {
  const displayName = normalizeTeamNameForDisplay(teamName);
  const seed = getTeamSeedNumber(teamName);
  return seed ? `(${seed}) ${displayName}` : displayName;
};

const RootingInterests: React.FC<RootingInterestsProps> = ({
  playerName,
  playerPicks,
  knownWinners,
  onClose,
  activePoolName
}) => {
  const [rootingInterests, setRootingInterests] = useState<RootingInterestsType | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(60);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Get the configuration from context
  const { roundConfigs, currentPointValues } = useConfig();

  // LRU cache for rooting interest results
  const rootingCache = useRootingCache();

  const startCountdown = useCallback(() => {
    setCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  }, []);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopCountdown();
  }, [stopCountdown]);

  useEffect(() => {
    // Reset state when player changes
    if (playerName) {
      setRootingInterests(null);
      setLoadingState('idle');
      setErrorMessage('');

      // Load rooting interests for selected player
      fetchRootingInterests();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerName, playerPicks, knownWinners]);

  const fetchRootingInterests = async () => {
    if (!playerName) return;

    try {
      // Extract round names and point values from config
      const roundNames = [
        "Round of 64",  // First round (fixed)
        ...roundConfigs.slice(0, 5).map(config => config.name)  // Next 5 rounds from config
      ];
      // Use current point values from context (6 values for scoring rounds)
      const pointsPerRound = [...currentPointValues]; // No prepending needed

      // Check LRU cache before calling API
      const cacheKey = buildRootingCacheKey(playerName, playerPicks, knownWinners, pointsPerRound, roundNames);
      const cachedResult = rootingCache.get(cacheKey);

      if (cachedResult) {
        setLoadingState('loading');
        await new Promise(resolve => setTimeout(resolve, 500));
        setRootingInterests(cachedResult);
        setLoadingState('success');
        return;
      }

      setLoadingState('loading');
      startCountdown();

      // Pass configuration to API
      const interests = await getPlayerRootingInterests(
        playerName,
        playerPicks,
        knownWinners,
        roundNames,
        pointsPerRound,
        activePoolName ?? undefined
      );

      stopCountdown();
      rootingCache.set(cacheKey, interests);
      setRootingInterests(interests);
      setLoadingState('success');
    } catch (error) {
      stopCountdown();
      console.error('Error fetching rooting interests:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      setLoadingState('error');
    }
  };

  if (!playerName) return null;

  // Format percentage to one decimal place
  const formatPercentage = (pct: number): string => {
    return (pct * 100).toFixed(0) + '%';
  };

  // Format the game name with the preferred team in bold and seed numbers
  const formatGameWithBoldTeam = (gameName: string, preferredTeam: string): React.ReactNode => {
    // Extract just the game information part (before the colon)
    const colonIndex = gameName.indexOf(':');
    if (colonIndex === -1) return gameName;

    const gamePrefix = gameName.substring(0, colonIndex + 1);
    const teamsInfo = gameName.substring(colonIndex + 1).trim();

    // Clean the preferred team name (remove asterisks) and normalize
    const cleanedPreferredTeam = cleanTeamName(preferredTeam);
    const normalizedPreferredTeam = normalizeTeamNameForDisplay(cleanedPreferredTeam);

    // Check if "vs" exists in the teamsInfo
    if (!teamsInfo.includes(" vs ")) {
      const cleanedTeam = cleanTeamName(teamsInfo);
      const displayTeam = formatTeamWithSeed(cleanedTeam);
      if (normalizeTeamNameForDisplay(cleanedTeam) === normalizedPreferredTeam) {
        return (
          <>
            {gamePrefix} <span className="font-bold">{displayTeam}</span>
          </>
        );
      }
      return <>{gamePrefix} {displayTeam}</>;
    }

    const [team1, team2] = teamsInfo.split(" vs ").map(team => cleanTeamName(team.trim()));
    const normalizedTeam1 = normalizeTeamNameForDisplay(team1);
    const normalizedTeam2 = normalizeTeamNameForDisplay(team2);
    const displayTeam1 = formatTeamWithSeed(team1);
    const displayTeam2 = formatTeamWithSeed(team2);

    // Check which team is the preferred one
    const team1IsPreferred = normalizedTeam1 === normalizedPreferredTeam;
    const team2IsPreferred = normalizedTeam2 === normalizedPreferredTeam;

    return (
      <>
        {gamePrefix} {team1IsPreferred ?
          <span className="font-bold">{displayTeam1}</span> : displayTeam1} vs {
          team2IsPreferred ?
          <span className="font-bold">{displayTeam2}</span> : displayTeam2}
      </>
    );
  };

  // Format no impact games with normalized team names and seeds
  const formatNoImpactGame = (gameName: string): React.ReactNode => {
    const colonIndex = gameName.indexOf(':');
    if (colonIndex === -1) return gameName;

    const gamePrefix = gameName.substring(0, colonIndex + 1);
    const teamsInfo = gameName.substring(colonIndex + 1);

    const teamParts = teamsInfo.split(' vs ').map(team => formatTeamWithSeed(team.trim()));

    return (
      <>
        {gamePrefix} {teamParts.join(' vs ')}
      </>
    );
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2">
            <DialogTitle>Rooting Interests for {playerName}</DialogTitle>
            <InfoTooltip
              content={
                <p>Games are ranked by impact. Yellow highlights indicate must-win games.</p>
              }
            />
          </div>
          <DialogDescription>
            See which upcoming games have the biggest impact on this player's chances.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-160px)] px-6 pb-6">
          {loadingState === 'loading' && (
            <div className="flex items-center gap-3 my-5">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-muted-foreground">
                Calculating rooting interests... {countdown > 0 ? `(${countdown}s)` : ''}
              </p>
            </div>
          )}

          {loadingState === 'error' && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 my-5 text-destructive text-sm">
              {errorMessage || 'An error occurred while fetching rooting interests.'}
            </div>
          )}

          {loadingState === 'success' && rootingInterests && (
            <>
              {rootingInterests.games.length > 0 ? (
                <div className="mb-5">
                  {(() => {
                    const maxImpact = Math.max(...rootingInterests.games.map(g => g.impact));
                    return (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Game (Winner in Bold)</TableHead>
                            <TableHead className="text-center">If Win</TableHead>
                            <TableHead className="text-center">If Lose</TableHead>
                            <TableHead className="text-center">Impact</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rootingInterests.games.map((game, index) => {
                            // Extract team from gameName if necessary
                            const gameNameParts = game.gameName.split(/\s{2,}/); // Split by multiple spaces
                            const actualGameName = gameNameParts[0];
                            const actualPreferredTeam = game.preferredTeam || (gameNameParts.length > 1 ? gameNameParts[1] : '');

                            // Clean the preferred team name for display
                            const cleanedPreferredTeam = cleanTeamName(actualPreferredTeam);

                            // Bar width as percentage of the max impact game
                            const barPercent = maxImpact > 0 ? (game.impact / maxImpact) * 100 : 0;

                            return (
                              <TableRow
                                key={index}
                                className={cn(game.isMustWin && "bg-warning/20")}
                              >
                                <TableCell>
                                  {formatGameWithBoldTeam(actualGameName, cleanedPreferredTeam)}
                                  {game.isMustWin && (
                                    <Badge variant="warning" className="ml-2 text-xs">
                                      Must Win
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-center font-medium">
                                  {formatPercentage(game.winPercentWithPreferred)}
                                </TableCell>
                                <TableCell className="text-center font-medium">
                                  {formatPercentage(game.winPercentWithoutPreferred)}
                                </TableCell>
                                <TableCell className="w-[100px]">
                                  <div className="flex items-center gap-2">
                                    <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                                      <div
                                        className="h-full rounded-full bg-primary transition-all"
                                        style={{ width: `${barPercent}%` }}
                                      />
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    );
                  })()}
                </div>
              ) : (
                <p className="italic text-muted-foreground">No games with impact found.</p>
              )}

              {rootingInterests.noImpactGames.length > 0 && (
                <div className="mt-5 rounded-md bg-muted p-4">
                  <h3 className="mt-0 text-base font-medium">Games with no impact on win probability:</h3>
                  <ul className="mt-2 ml-5 list-disc text-sm text-muted-foreground">
                    {rootingInterests.noImpactGames.map((game, index) => (
                      <li key={index}>{formatNoImpactGame(game)}</li>
                    ))}
                  </ul>
                </div>
              )}

            </>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RootingInterests;
